import React, { FC, useRef, useState } from 'react';
import Modal from '../components/overlay/Modal';
import Subheading from '../components/font/Subheading';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

import Dropdown from '../components/dropdown/Dropdown';
import DropdownItem from '../components/dropdown/DropdownItem';
import ColumnItem from '../components/layout/ColumnItem';
import Row from '../components/layout/Row';
import Spacer from '../components/layout/Spacer';
import { defaultCountries } from '../utils/default.countries';
import RadioButtonWrapper from '../components/radio/RadioButtonWrapper';
import RadioButton from '../components/radio/RadioButton';
import RadioButtonContent from '../components/radio/RadioButtonContent';
import { CreateShopRequest } from '../requests/create.shop.request';
import { useAppDispatch } from '../redux/redux.hooks';
import { createNewShop } from '../reducers/new.shop.reducer';
import { P } from '../components/font/Parargraph';
import { useLoggedIn } from '../hooks/use.logged.in';
import { useClickOutside } from '../hooks/use.click.outside';
import HomeButton from '../components/header/HomeButton';
import { useToast } from '../components/toast/ToastProvider';

type NewShopProps = {};

const NewShop: FC<NewShopProps> = () => {
  const initialRequest: CreateShopRequest = {
    name: '',
    domain: '',
    defaultCountry: 'US',
    dynamicSizing: true,
  };

  useLoggedIn();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const countryRef = useRef<HTMLDivElement>(null);
  const [requestData, setRequestData] =
    useState<CreateShopRequest>(initialRequest);
  const [nameHasError, setNameHasError] = useState<boolean>(false);
  const [domainHasError, setDomainHasError] = useState<boolean>(false);
  const [showResult, setShowResult] = useState<boolean>(false);
  const [showDefaultCountries, setShowDefaultCountries] =
    useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [result, setResult] = useState<{
    shop_id: string;
    shop_domain: string;
    defaultCountry: string;
    dynamicSizing: boolean;
  }>({
    shop_id: '',
    shop_domain: requestData.domain,
    defaultCountry: requestData.defaultCountry,
    dynamicSizing: requestData.dynamicSizing,
  });

  useClickOutside(countryRef, () => setShowDefaultCountries(false));

  const nameError = 'Please enter the shop name';
  const domainError = "Please enter the shop's domain";

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const response = await dispatch(createNewShop(requestData)).unwrap();
      setSubmitting(false);
      setShowResult(true);
      setResult({
        shop_id: response.shop_id,
        shop_domain: response.shop_domain,
        defaultCountry: response.default_country,
        dynamicSizing: response.dynamic_sizing,
      });
    } catch (error) {
      toast.show('Failed to create store 😢', { isError: true });
    }
  };

  const renderDefaultCountries = () => (
    <Dropdown ref={countryRef}>
      {defaultCountries.map((c) => (
        <DropdownItem
          key={c.value}
          onClick={() =>
            setRequestData({ ...requestData, defaultCountry: c.value })
          }
        >
          <Subheading>{c.value}</Subheading>
        </DropdownItem>
      ))}
    </Dropdown>
  );

  const renderModal = () => (
    <Modal
      open={showResult}
      onClose={() => setShowResult(false)}
      heading='Result'
      primaryAction={{
        content: 'Return home',
        onAction: () => navigate('/neworexisting'),
      }}
    >
      <>
        <Row>
          <ColumnItem>
            <Subheading>Shop ID</Subheading>
            <P>{result.shop_id}</P>

            <Spacer height={8} />
            <Subheading>Shop url</Subheading>
            <P>{result.shop_domain}</P>

            <Spacer height={8} />
            <Subheading>Default country</Subheading>
            <P>{result.defaultCountry}</P>

            <Spacer height={8} />
            <Subheading>Dynamic sizing</Subheading>
            <P>{result.dynamicSizing ? 'Yes' : 'No'}</P>
          </ColumnItem>
        </Row>
      </>
    </Modal>
  );

  return (
    <s-page>
      {showResult && renderModal()}
      <SettingsContainer>
        <Row>
          <ColumnItem>
            <HomeButton title='Create new shop' />
            <Spacer height={8} />
            <P>This is used for creating custom shops and size guides</P>
          </ColumnItem>
        </Row>
        <Spacer height={16} />
        <Row>
          <ColumnItem>
            <s-text-field
              id='shopName'
              name='shopName'
              label='Shop name'
              value={requestData.name}
              onInput={(event) => {
                setRequestData({ ...requestData, name: event.currentTarget.value });
              }}
              onBlur={() => setNameHasError(requestData.name === '')}
              error={nameHasError ? nameError : ''}
              autocomplete='off'
            />
          </ColumnItem>
          <ColumnItem>
            <s-text-field
              id='shopDomain'
              name='shopDomain'
              label='Shop URL'
              value={requestData.domain}
              onInput={(event) => {
                setRequestData({ ...requestData, domain: event.currentTarget.value });
              }}
              onBlur={() => setDomainHasError(requestData.domain === '')}
              error={domainHasError ? domainError : ''}
              autocomplete='off'
            />
          </ColumnItem>
        </Row>
        <Row>
          <ColumnItem>
            <Subheading>DEFAULT COUNTRY</Subheading>
            <Spacer height={8} />
            <Row>
              <RadioButtonWrapper>
                <DefaultCountryContainer>
                  <RadioButton
                    groupName='countries'
                    value={requestData.defaultCountry}
                    onClick={() =>
                      setShowDefaultCountries(!showDefaultCountries)
                    }
                    content={() => (
                      <RadioButtonContent>
                        <Subheading>{requestData.defaultCountry}</Subheading>
                      </RadioButtonContent>
                    )}
                  />
                  {showDefaultCountries && renderDefaultCountries()}
                </DefaultCountryContainer>
              </RadioButtonWrapper>
            </Row>
          </ColumnItem>
          <ColumnItem>
            <Subheading>DYNAMIC SIZES</Subheading>
            <Spacer height={8} />
            <Row>
              <RadioButtonWrapper>
                <RadioButton
                  groupName='dynamicSizes'
                  value='true'
                  checked={requestData.dynamicSizing}
                  onClick={() =>
                    setRequestData({ ...requestData, dynamicSizing: true })
                  }
                  content={() => <RadioButtonContent>Yes</RadioButtonContent>}
                />
              </RadioButtonWrapper>
              <RadioButtonWrapper>
                <RadioButton
                  groupName='dynamicSizes'
                  value='false'
                  checked={!requestData.dynamicSizing}
                  onClick={() =>
                    setRequestData({ ...requestData, dynamicSizing: false })
                  }
                  content={() => <RadioButtonContent>No</RadioButtonContent>}
                />
              </RadioButtonWrapper>
            </Row>
          </ColumnItem>
        </Row>
      </SettingsContainer>
      <ButtonContainer>
        <s-button
          loading={submitting}
          disabled={
            requestData.domain === '' || requestData.name === '' || submitting
          }
          variant='primary'
          onClick={handleSubmit}
        >
          Complete
        </s-button>
      </ButtonContainer>
    </s-page>
  );
};

export default NewShop;

const SettingsContainer = styled.div`
  background: #fafafa;
  border-radius: 12px;
  box-shadow: 0 0 10px #e6e6e6;
  padding-bottom: 24px;
`;

const DefaultCountryContainer = styled.div`
  width: fit-content;
  max-height: 36px;
`;
const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  margin-top: 12px;
`;
